import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import PackageCard from "@/components/PackageCard";
import TestimonialsSection from "@/components/TestimonialsSection";
import CountdownSection from "@/components/CountdownSection";
import SEO from "@/components/SEO";
import HeroSection from "@/components/home/HeroSection";
import WhyUsSection from "@/components/home/WhyUsSection";
import CategoriesSection from "@/components/home/CategoriesSection";
import FasilitasSection from "@/components/home/FasilitasSection";
import UstadzSection from "@/components/home/UstadzSection";
import PromoSection from "@/components/home/PromoSection";
import PopupAd from "@/components/home/PopupAd";
import RollingAdBar from "@/components/home/RollingAdBar";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const { lang, t } = useLang();
  const [featured, setFeatured] = React.useState([]);
  const [gallery, setGallery] = React.useState([]);
  const [posts, setPosts] = React.useState([]);

  React.useEffect(() => {
    Promise.all([
      api.get("/packages/featured"),
      api.get("/gallery"),
      api.get("/blog"),
    ])
      .then(([f, g, b]) => {
        setFeatured(f.data);
        setGallery(g.data.slice(0, 6));
        setPosts(b.data.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <RollingAdBar />
      <PopupAd />
      <SEO
        title="Travel Umrah & Haji Sesuai Sunnah"
        titleEn="Sunnah-Aligned Umrah & Hajj Travel"
        description="BATIK Tour & Travel (PT Baitullah Tiga Kharisma) — travel umrah & haji resmi berizin PPIU & PIHK Kementerian Agama RI. Paket Silver, Gold, Platinum, Titanium, Umroh Plus, Haji Plus & Haji Furoda dengan bimbingan ustadz berpengalaman."
        descriptionEn="BATIK Tour & Travel (PT Baitullah Tiga Kharisma) — officially licensed Umrah & Hajj agent (PPIU & PIHK) by Indonesia's Religious Ministry. Silver, Gold, Platinum, Titanium, Umrah Plus, Hajj Plus & Furoda packages with experienced ustadz guides."
      />

      <HeroSection />

      {/* JADWAL KEBERANGKATAN + COUNTDOWN — dipindah ke atas biar jadi CTA utama */}
      <CountdownSection />

      {/* PROMO TERBARU — auto-hidden kalau belum ada poster yang tayang */}
      <PromoSection />

      <CategoriesSection />

      <WhyUsSection />

      <FasilitasSection />

      {/* FEATURED PACKAGES — 3 packages only, click "Lihat Semua" for the rest */}
      <section className="py-24 lg:py-32 bg-muted/30" data-testid="featured-packages-section">
        <div className="container-x">
          <header className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div className="max-w-xl">
              <div className="flex items-center gap-4 mb-4">
                <span className="h-px w-10 bg-accent" />
                <p className="overline">{t("sections.featuredOver")}</p>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">{t("sections.featuredTitle")}</h2>
              <p className="mt-5 text-muted-foreground text-lg">{t("sections.featuredSub")}</p>
            </div>
            <Link to="/packages" className="btn-outline whitespace-nowrap" data-testid="featured-view-all">
              {t("common.viewAll")} <ArrowRight className="h-4 w-4" />
            </Link>
          </header>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">
            {featured.slice(0, 3).map((p, i) => (<PackageCard key={p.id} pkg={p} index={i} />))}
          </div>
          {featured.length > 3 && (
            <div className="mt-12 text-center">
              <Link to="/packages" className="btn-primary inline-flex items-center gap-2" data-testid="featured-see-more">
                {lang === "id" ? "Lihat Semua Paket" : "See All Packages"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </section>

      <UstadzSection />

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* GALLERY PREVIEW */}
      <section className="py-24 lg:py-32" data-testid="gallery-preview-section">
        <div className="container-x">
          <header className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div className="max-w-xl">
              <div className="flex items-center gap-4 mb-4">
                <span className="h-px w-10 bg-accent" />
                <p className="overline">{t("sections.galleryOver")}</p>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">{t("sections.galleryTitle")}</h2>
              <p className="mt-5 text-muted-foreground text-lg">{t("sections.gallerySub")}</p>
            </div>
            <Link to="/gallery" className="btn-outline whitespace-nowrap" data-testid="gallery-view-all">
              {t("common.viewAll")} <ArrowRight className="h-4 w-4" />
            </Link>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {gallery.map((g, i) => (
              <Link
                key={g.id}
                to="/gallery"
                data-testid={`gallery-tile-${g.id}`}
                className={`relative overflow-hidden rounded-lg group ${i === 0 ? "col-span-2 row-span-2" : ""}`}
              >
                <img
                  src={g.image}
                  alt={lang === "id" ? g.title_id : g.title_en}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 aspect-square"
                />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/30 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-24 lg:py-32 bg-muted/40" data-testid="blog-preview-section">
        <div className="container-x">
          <header className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div className="max-w-xl">
              <div className="flex items-center gap-4 mb-4">
                <span className="h-px w-10 bg-accent" />
                <p className="overline">{t("sections.blogOver")}</p>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">{t("sections.blogTitle")}</h2>
              <p className="mt-5 text-muted-foreground text-lg">{t("sections.blogSub")}</p>
            </div>
            <Link to="/blog" className="btn-outline whitespace-nowrap" data-testid="blog-view-all">
              {t("common.viewAll")} <ArrowRight className="h-4 w-4" />
            </Link>
          </header>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <Link
                to={`/blog/${p.slug}`}
                key={p.id}
                data-testid={`blog-tile-${p.slug}`}
                className="group bg-card border border-border rounded-lg overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 anim-fade-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="overline mb-3">{p.category} · {p.published_at}</p>
                  <h3 className="font-serif text-xl text-primary leading-snug">{lang === "id" ? p.title_id : p.title_en}</h3>
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{lang === "id" ? p.excerpt_id : p.excerpt_en}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
